"use client";

import { fetchUserData, updateUser } from "@/app/product/utils/user.utils";
import { validateReferralCode } from "@/app/product/_actions/user.actions";
import { useEffect, useState, useCallback, useRef } from "react";
import { useFormik, type FormikErrors } from "formik";
import { Listbox, Transition } from "@headlessui/react";
import { ChevronDown, Check } from "lucide-react";
import { clientLogger } from "@/lib/logger";
import { useAppData } from "@/store/appStore";
import { Logo } from "@/design-elements/logo";
import { trackEvent, GTMEvent } from "@/lib/utils/gtm-events";
import { REFERRAL_STORAGE_KEY } from "@/components/tracking/UTMTracker";
import Image from "next/image";

interface StepUserInfoProps {
	onSubmit: () => void;
}

interface FormValues {
	firstName: string;
	lastName: string;
	roleId: string;
	referralCode: string;
}

const NAME_MAX_LENGTH = 50;
const NAME_ALLOWED_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ''\- ]+$/;

const USER_INFO_COPY = {
	heroTitle: "Tell us a bit about yourself",
	heroSubtitle: "We'll find deals tailored for you and\nyour company policy",
	firstNameLabel: "First name",
	firstNamePlaceholder: "John",
	lastNameLabel: "Last name",
	lastNamePlaceholder: "Doe",
	rolePlaceholder: "Select",
	submitting: "Submitting...",
	submit: "Next",
	skip: "Skip",
	referralLabel: "Have a referral code? (optional)",
	referralPlaceholder: "Referral code",
	referralTooltip:
		"Already have AutoMint users at your company? Get a referral code from them and see what rewards come your way. T&Cs apply",
	referralInvalid: "This is not a valid referral code",
	referralSuccess:
		"Hurray! You'll get a ₹1,000 Amazon voucher on signing up. T&Cs apply",
};

const getNameError = (label: string, value: string): string => {
	const trimmed = value.trim();
	if (!trimmed) return `Please enter your ${label}.`;
	if (trimmed.length < 2) return `${label} must be at least 2 characters.`;
	if (trimmed.length > NAME_MAX_LENGTH)
		return `${label} must be at most ${NAME_MAX_LENGTH} characters.`;
	if (!NAME_ALLOWED_REGEX.test(trimmed))
		return `${label} can include letters, spaces, hyphens, and apostrophes only.`;
	if (/\s{2,}/.test(trimmed))
		return `Use single spaces in your ${label.toLowerCase()}.`;
	if (/^[-'']|[-'']$/.test(trimmed))
		return `${label} cannot start or end with a hyphen or apostrophe.`;
	return "";
};

const StepUserInfo: React.FC<StepUserInfoProps> = ({ onSubmit }) => {
	const [appData, , setCookielessData] = useAppData();
	const [user, setUser] = useState<User | undefined>(undefined);
	// Role fetch disabled: GET /employer/:id/roles is admin-only, so it always 403s for employees.
	const [roleList] = useState<Role[]>([]);
	const [submitError, setSubmitError] = useState("");
	const [showReferralTooltip, setShowReferralTooltip] = useState(false);
	const [referral, setReferral] = useState<{
		status: "idle" | "validating" | "valid" | "invalid";
		message: string;
	}>({ status: "idle", message: "" });

	useEffect(() => {
		fetchUserData({ setUser });
	}, []);

	useEffect(() => {
		if (!user?.email) return;
		const storedRef = sessionStorage.getItem(REFERRAL_STORAGE_KEY);
		if (!storedRef) return;

		setReferral({ status: "validating", message: "" });
		validateReferralCode({
			referralCode: storedRef,
			refereeEmail: user.email,
		}).then((result) => {
			if (result.success) {
				formik.setFieldValue(
					"referralCode",
					result.data?.referrerEmail || storedRef,
					false,
				);
				setReferral({
					status: "valid",
					message: result.data?.message || "",
				});
			} else {
				setReferral({
					status: "invalid",
					message: result.errorText || USER_INFO_COPY.referralInvalid,
				});
			}
			sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.email]);

	const shouldSelectRole = Boolean(
		user?.employment?.company?.isCompanyRolesActive &&
			!user?.employment?.companyRole &&
			roleList.length > 0,
	);

	const formik = useFormik<FormValues>({
		enableReinitialize: true,
		initialValues: {
			firstName: user?.firstName || "",
			lastName: user?.lastName || "",
			roleId: user?.employment?.companyRole?.id?.toString() || "",
			referralCode: "",
		},
		validateOnMount: true,
		validate: (values) => {
			const errors: FormikErrors<FormValues> = {};
			const firstErr = getNameError("first name", values.firstName);
			if (firstErr) errors.firstName = firstErr;
			const lastErr = getNameError("last name", values.lastName);
			if (lastErr) errors.lastName = lastErr;
			if (shouldSelectRole && !values.roleId) {
				errors.roleId = "Please select your role.";
			}
			return errors;
		},
		onSubmit: async (values, { setSubmitting, setFieldError }) => {
			setSubmitError("");
			try {
				const response = await updateUser({
					firstName: values.firstName.trim(),
					lastName: values.lastName.trim(),
					companyRoleId: values.roleId || undefined,
					referralCode:
						referral.status === "valid"
							? values.referralCode.trim()
							: undefined,
				});

				if (!response?.success) {
					const message =
						response?.error || "Something went wrong. Please try again.";
					setSubmitError(message);
					setFieldError("firstName", "");
					setSubmitting(false);
					return;
				}

				const nameChanged =
					values.firstName.trim() !== user?.firstName ||
					values.lastName.trim() !== user?.lastName;

				if (nameChanged) {
					trackEvent(GTMEvent.NAME_UPDATED, { page_path: "Onboarding page" });
				}

				if (referral.status === "valid" && values.referralCode.trim()) {
					trackEvent(GTMEvent.SIGNUP_REFERRAL, {
						referee: user?.email || "",
						referrer: values.referralCode.trim(),
					});
				}

				if (appData?.user) {
					setCookielessData({
						user: {
							...appData.user,
							firstName: values.firstName.trim(),
							lastName: values.lastName.trim(),
						},
					});
				}

				clientLogger.info("Onboarding: User info submitted");
				onSubmit();
			} catch (error) {
				clientLogger.info("Error updating user data", error as Error);
				setSubmitError("Network error. Please try again.");
			} finally {
				setSubmitting(false);
			}
		},
	});

	const formikRef = useRef(formik);
	formikRef.current = formik;

	const handleReferralBlur = useCallback(
		async (e: React.FocusEvent<HTMLInputElement>) => {
			formikRef.current.handleBlur(e);
			const code = e.target.value.trim();
			if (!code) {
				setReferral({ status: "idle", message: "" });
				return;
			}
			if (!user?.email) return;

			setReferral({ status: "validating", message: "" });
			const result = await validateReferralCode({
				referralCode: code,
				refereeEmail: user.email,
			});

			if (result.success) {
				if (result.data?.referrerEmail) {
					formikRef.current.setFieldValue(
						"referralCode",
						result.data.referrerEmail,
						false,
					);
				}
				setReferral({
					status: "valid",
					message: result.data?.message || "",
				});
			} else {
				setReferral({
					status: "invalid",
					message: result.errorText || USER_INFO_COPY.referralInvalid,
				});
			}
		},
		[user?.email],
	);

	const clearReferral = useCallback(() => {
		formikRef.current.setFieldValue("referralCode", "", false);
		setReferral({ status: "idle", message: "" });
	}, []);

	const handleSkip = () => {
		formik.resetForm();
		clientLogger.info("Onboarding: User info step skipped");
		onSubmit();
	};

	const selectedRoleLabel =
		roleList.find((r) => r.id.toString() === formik.values.roleId)?.title ||
		USER_INFO_COPY.rolePlaceholder;

	const roleLabel =
		user?.employment?.company?.companyRoleLabel ?? "designation/band/grade";

	const isReferralValid = referral.status === "valid";
	const isReferralInvalid = referral.status === "invalid";
	const submitDisabled =
		!formik.isValid ||
		formik.isSubmitting ||
		referral.status === "invalid" ||
		referral.status === "validating" ||
		(!!formik.values.referralCode.trim() && referral.status === "idle");
	const showSubmitDisabledStyle = submitDisabled && !formik.isSubmitting;

	return (
		<section className="relative flex flex-col items-stretch w-full h-full bg-[var(--automint-onboarding-bg)] text-[var(--automint-black)] md:bg-[var(--automint-black)] md:text-white rounded-[6px] overflow-hidden">
			<div className="flex flex-col flex-1 justify-center md:justify-start px-5 md:px-7 py-6 md:py-8">
				<div className="mx-auto">
					<Logo
						showText={true}
						invert
						size={40}
						className="[&_span]:!text-[var(--automint-black)] md:[&_span]:!text-white [&_span]:!text-2xl [&_span]:!font-medium"
					/>
				</div>

				<div className="flex flex-col md:flex-1 md:justify-center">
					<div className="mt-10 md:mt-0 text-center">
						<h1 className="text-[26px] md:text-[28px]  leading-tight">
							{USER_INFO_COPY.heroTitle}
						</h1>
						<p className="text-sm text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-subtitle-dark)] mt-3 whitespace-pre-line">
							{USER_INFO_COPY.heroSubtitle}
						</p>
					</div>

					<div className="mt-6 space-y-4">
						{/* First name */}
						<div>
							<label
								htmlFor="onboarding_user_first_name"
								className="block text-sm text-[var(--automint-black)] md:text-white mb-2"
							>
								{USER_INFO_COPY.firstNameLabel}
							</label>
							<input
								id="onboarding_user_first_name"
								name="firstName"
								type="text"
								autoComplete="given-name"
								autoFocus
								placeholder={USER_INFO_COPY.firstNamePlaceholder}
								value={formik.values.firstName}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								disabled={formik.isSubmitting}
								onKeyDown={(e) => {
									if (e.key === "Enter" && !formik.isSubmitting) {
										formik.handleSubmit();
									}
								}}
								className="w-full h-12 rounded-[6px] bg-[var(--colors-automint-neutral-200)] border border-[var(--colors-automint-neutral-200)] text-[var(--automint-black)] placeholder:text-[var(--colors-automint-neutral-500)] md:bg-[var(--colors-automint-neutral-800)] md:border-[var(--colors-automint-neutral-800)] md:text-white md:placeholder:text-[var(--automint-onboarding-placeholder-dark)] px-4 text-[15px] focus:outline-none focus:border-[var(--automint-black)] md:focus:border-[var(--automint-onboarding-focus-border-dark)] disabled:opacity-60"
							/>
							{formik.touched.firstName && formik.errors.firstName && (
								<p className="text-[var(--automint-error-text-light)] md:text-[var(--automint-error-text-dark)] text-xs mt-1">
									{formik.errors.firstName}
								</p>
							)}
						</div>

						{/* Last name */}
						<div>
							<label
								htmlFor="onboarding_user_last_name"
								className="block text-sm text-[var(--automint-black)] md:text-white mb-2"
							>
								{USER_INFO_COPY.lastNameLabel}
							</label>
							<input
								id="onboarding_user_last_name"
								name="lastName"
								type="text"
								autoComplete="family-name"
								placeholder={USER_INFO_COPY.lastNamePlaceholder}
								value={formik.values.lastName}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								disabled={formik.isSubmitting}
								onKeyDown={(e) => {
									if (e.key === "Enter" && !formik.isSubmitting) {
										formik.handleSubmit();
									}
								}}
								className="w-full h-12 rounded-[6px] bg-[var(--colors-automint-neutral-200)] border border-[var(--colors-automint-neutral-200)] text-[var(--automint-black)] placeholder:text-[var(--colors-automint-neutral-500)] md:bg-[var(--colors-automint-neutral-800)] md:border-[var(--colors-automint-neutral-800)] md:text-white md:placeholder:text-[var(--automint-onboarding-placeholder-dark)] px-4 text-[15px] focus:outline-none focus:border-[var(--automint-black)] md:focus:border-[var(--automint-onboarding-focus-border-dark)] disabled:opacity-60"
							/>
							{formik.touched.lastName && formik.errors.lastName && (
								<p className="text-[var(--automint-error-text-light)] md:text-[var(--automint-error-text-dark)] text-xs mt-1">
									{formik.errors.lastName}
								</p>
							)}
						</div>

						{/* Role select - dark themed Listbox */}
						{shouldSelectRole && (
							<div>
								<label className="block text-sm text-[var(--automint-black)] md:text-white mb-2">
									Current {roleLabel}
								</label>
								<Listbox
									value={formik.values.roleId}
									onChange={(value) => {
										formik.setFieldValue("roleId", value, true);
										formik.setFieldTouched("roleId", true, false);
										setSubmitError("");
									}}
									disabled={formik.isSubmitting}
								>
									<div className="relative">
										<Listbox.Button className="w-full h-12 rounded-[6px] bg-[var(--colors-automint-neutral-200)] border border-[var(--colors-automint-neutral-200)] text-[var(--automint-black)] md:bg-[var(--colors-automint-neutral-800)] md:border-[var(--colors-automint-neutral-800)] md:text-white px-4 text-left flex items-center justify-between focus:outline-none focus:border-[var(--automint-black)] md:focus:border-[var(--automint-onboarding-focus-border-dark)]">
											<span
												className={`truncate text-[15px] ${formik.values.roleId ? "text-[var(--automint-black)] md:text-white" : "text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-placeholder-dark)]"}`}
											>
												{selectedRoleLabel}
											</span>
											<ChevronDown className="h-4 w-4 text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-subtitle-dark)]" />
										</Listbox.Button>
										<Transition
											leave="transition ease-in duration-100"
											leaveFrom="opacity-100"
											leaveTo="opacity-0"
										>
											<Listbox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-[6px] border border-[var(--colors-automint-neutral-200)] bg-white text-[var(--automint-black)] md:border-[var(--colors-automint-neutral-800)] md:bg-[var(--colors-automint-neutral-800)] md:text-white py-1 shadow-lg focus:outline-none">
												{roleList.map((role) => (
													<Listbox.Option
														key={role.id}
														value={role.id.toString()}
														className={({ active, selected }) =>
															`cursor-pointer select-none px-4 py-2 text-sm flex items-center justify-between ${
																active
																	? "bg-[var(--automint-onboarding-listbox-hover-light)] text-[var(--automint-black)] md:bg-[var(--automint-onboarding-disabled-bg)] md:text-white"
																	: "text-[var(--automint-black)] md:text-white"
															} ${selected ? "font-medium" : ""}`
														}
													>
														{({ selected }) => (
															<>
																<span className="truncate">{role.title}</span>
																{selected && (
																	<Check className="h-4 w-4 text-[var(--colors-automint-neon-500)]" />
																)}
															</>
														)}
													</Listbox.Option>
												))}
											</Listbox.Options>
										</Transition>
									</div>
								</Listbox>
								{formik.touched.roleId && formik.errors.roleId && (
									<p className="text-[var(--automint-error-text-light)] md:text-[var(--automint-error-text-dark)] text-xs mt-1">
										{formik.errors.roleId}
									</p>
								)}
							</div>
						)}

						{/* Referral code (optional, preserved from original flow even though
						not in the new design) */}
						<div className="relative">
							<label
								htmlFor="onboarding_referral_code"
								className="block text-sm text-[var(--automint-black)] md:text-white mb-2"
							>
								<span
									className="inline-flex items-center gap-1 cursor-pointer"
									onMouseEnter={() => setShowReferralTooltip(true)}
									onMouseLeave={() => setShowReferralTooltip(false)}
									onClick={(e) => {
										e.stopPropagation();
										setShowReferralTooltip((s) => !s);
									}}
								>
									{USER_INFO_COPY.referralLabel}
									<Image
										src="/new_icons/general/i-icon.svg"
										alt="info"
										width={14}
										height={14}
										className="flex-shrink-0 opacity-80 md:invert"
									/>
								</span>
							</label>

							{showReferralTooltip && (
								<>
									<div
										className="fixed inset-0 z-[9] md:hidden"
										onClick={(e) => {
											e.stopPropagation();
											setShowReferralTooltip(false);
										}}
									/>
									<div
										className="absolute bottom-[calc(100%+4px)] left-0 z-30"
										onMouseEnter={() => setShowReferralTooltip(true)}
										onMouseLeave={() => setShowReferralTooltip(false)}
									>
										<div className="bg-white text-[var(--automint-black)] rounded-[6px] p-3 w-[240px] md:w-[280px] shadow-lg">
											<p className="text-[11px] md:text-xs leading-relaxed">
												{USER_INFO_COPY.referralTooltip}
											</p>
										</div>
									</div>
								</>
							)}

							{isReferralValid ? (
								<div className="relative">
									<input
										id="onboarding_referral_code"
										name="referralCode"
										value={formik.values.referralCode}
										disabled
										readOnly
										className="w-full h-12 rounded-[6px] bg-[var(--automint-success-bg-light)] border border-[var(--automint-success-text-light)] text-[var(--automint-success-text-light)] placeholder:text-[var(--colors-automint-neutral-500)] md:bg-[color-mix(in_srgb,var(--automint-success-bg-dark)_30%,transparent)] md:text-[var(--automint-success-text-dark)] md:placeholder:text-[var(--automint-onboarding-placeholder-dark)] px-4 pr-10 text-[15px] focus:outline-none"
									/>
									<button
										type="button"
										onClick={clearReferral}
										aria-label="Clear referral"
										className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 rounded-full bg-[color-mix(in_srgb,var(--automint-success-text-light)_15%,transparent)] text-[var(--automint-success-text-light)] hover:bg-[color-mix(in_srgb,var(--automint-success-text-light)_25%,transparent)] md:bg-[color-mix(in_srgb,var(--automint-success-text-light)_40%,transparent)] md:text-[var(--automint-success-text-dark)] md:hover:bg-[color-mix(in_srgb,var(--automint-success-text-light)_60%,transparent)]"
									>
										<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
											<path
												d="M3 3L9 9M9 3L3 9"
												stroke="currentColor"
												strokeWidth="1.5"
												strokeLinecap="round"
											/>
										</svg>
									</button>
								</div>
							) : (
								<div className="relative">
									<input
										id="onboarding_referral_code"
										name="referralCode"
										placeholder={USER_INFO_COPY.referralPlaceholder}
										value={formik.values.referralCode}
										onChange={(e) => {
											formik.handleChange(e);
											if (referral.status !== "idle") {
												setReferral({ status: "idle", message: "" });
											}
										}}
										onBlur={handleReferralBlur}
										disabled={formik.isSubmitting}
										onKeyDown={(e) => {
											if (e.key === "Enter" && !formik.isSubmitting) {
												formik.handleSubmit();
											}
										}}
										className={`w-full h-12 rounded-[6px] bg-[var(--colors-automint-neutral-200)] text-[var(--automint-black)] placeholder:text-[var(--colors-automint-neutral-500)] md:bg-[var(--colors-automint-neutral-800)] md:text-white md:placeholder:text-[var(--automint-onboarding-placeholder-dark)] px-4 pr-10 text-[15px] focus:outline-none border ${
											isReferralInvalid
												? "border-[var(--automint-error-text-light)] focus:border-[var(--automint-error-text-light)] md:border-[var(--automint-error-text-dark)] md:focus:border-[var(--automint-error-text-dark)]"
												: "border-[var(--colors-automint-neutral-200)] focus:border-[var(--automint-black)] md:border-[var(--colors-automint-neutral-800)] md:focus:border-[var(--automint-onboarding-focus-border-dark)]"
										}`}
									/>
									{referral.status === "validating" && (
										<div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-[var(--colors-automint-neutral-300)] border-t-[var(--automint-black)] md:border-[var(--automint-onboarding-disabled-bg)] md:border-t-[var(--colors-automint-neon-500)] rounded-full animate-spin" />
									)}
									{isReferralInvalid && (
										<button
											type="button"
											onClick={clearReferral}
											aria-label="Clear referral"
											className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 rounded-full bg-[color-mix(in_srgb,var(--automint-error-text-light)_15%,transparent)] text-[var(--automint-error-text-light)] hover:bg-[color-mix(in_srgb,var(--automint-error-text-light)_25%,transparent)] md:bg-[color-mix(in_srgb,var(--automint-error-bg-dark)_40%,transparent)] md:text-[var(--automint-error-text-dark)] md:hover:bg-[color-mix(in_srgb,var(--automint-error-bg-dark)_60%,transparent)]"
										>
											<svg
												width="12"
												height="12"
												viewBox="0 0 12 12"
												fill="none"
											>
												<path
													d="M3 3L9 9M9 3L3 9"
													stroke="currentColor"
													strokeWidth="1.5"
													strokeLinecap="round"
												/>
											</svg>
										</button>
									)}
								</div>
							)}

							{isReferralInvalid && (
								<p className="text-[var(--automint-error-text-light)] md:text-[var(--automint-error-text-dark)] text-xs mt-1">
									{referral.message || USER_INFO_COPY.referralInvalid}
								</p>
							)}
							{isReferralValid && referral.message && (
								<p className="text-[var(--automint-success-text-light)] md:text-[var(--automint-success-text-dark)] text-xs mt-1">
									{referral.message}
								</p>
							)}
						</div>
					</div>

					{submitError && (
						<p className="text-[var(--automint-error-text-light)] md:text-[var(--automint-error-text-dark)] text-sm mt-3">
							{submitError}
						</p>
					)}

					<div className="mt-5">
						<button
							id="onboarding_user_info_submit"
							type="button"
							onClick={() => formik.handleSubmit()}
							disabled={submitDisabled}
							className={`w-full h-12 rounded-[6px] text-center font-medium text-sm leading-normal transition-colors disabled:cursor-not-allowed bg-[var(--automint-black)] text-[var(--colors-automint-neon-500)] md:bg-[var(--colors-automint-neon-500)] md:text-[var(--automint-black)] ${showSubmitDisabledStyle ? "opacity-15 md:opacity-100 md:!bg-[var(--automint-onboarding-disabled-bg)] md:!text-[var(--automint-onboarding-disabled-text)]" : ""}`}
						>
							{formik.isSubmitting
								? USER_INFO_COPY.submitting
								: USER_INFO_COPY.submit}
						</button>
					</div>

					<div className="mt-4 mb-2 text-center">
						<button
							id="onboarding_user_info_skip"
							type="button"
							onMouseDown={(e) => e.preventDefault()}
							onClick={handleSkip}
							className="text-[var(--automint-black)] md:text-white text-sm underline underline-offset-4 hover:opacity-80"
						>
							{USER_INFO_COPY.skip}
						</button>
					</div>
				</div>
			</div>
		</section>
	);
};

export default StepUserInfo;
